import { DeleteOutline as DeleteOutlinedIcon } from '@mui/icons-material';
import { Box, Button, FormControlLabel, Grid } from '@mui/material';
import React, { useContext } from 'react';
import { useState } from 'react';
import { AuthDataProvider } from 'src/components/authProvider';
import {
  NumberInputWidget,
  ResourceSelectorSidebar,
} from 'src/components/widget';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import { useGetAllAssets } from 'src/hooks';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import { lang } from 'src/utils';

import {
  KeyOfOptionalVisualItemsInterface,
  OptionalVisualItemsInterface,
} from '../ListingWithOptionalVisuals.interface';
import { CustomImageSourceProps } from './CustomImageSource.interface';
import { GridContainer, GridVisualPath } from './CustomImageSource.styled';

export const CustomImageSource = React.forwardRef(
  (props: CustomImageSourceProps, ref) => {
    const { data: assets = [] } = useGetAllAssets();
    const { userRole } = useContext(AuthDataProvider);

    const [showResourceSelector, setShowResourceSelector] = useState(false);

    const [visualItems, setVisualItems] = useState<
      OptionalVisualItemsInterface[]
    >(props.value);

    const updateVisualItems = (
      visualsItems: OptionalVisualItemsInterface[],
    ) => {
      setVisualItems(visualsItems);
      props.onChange(visualsItems);
    };

    const onChangedField = (
      assetName: string,
      key: KeyOfOptionalVisualItemsInterface,
      value: string,
    ) => {
      const updatedVisuals = visualItems.map((item) => {
        if (item.assetName === assetName) {
          return {
            ...item,
            style: {
              ...item.style,
              [key]: value,
            },
          };
        } else return item;
      });

      updateVisualItems(updatedVisuals);
    };

    const deleteVisualItem = (assetName: string) => {
      const updatedVisuals = visualItems?.filter(
        (item) => item.assetName !== assetName,
      );

      updateVisualItems(updatedVisuals);
    };

    const onAddAsset = (fileName: string) => {
      // Find the asset in the list of all assets
      const selectedAsset = assets.find((asset) => asset.fileName === fileName);

      // If the asset is already in the visuals, update it
      let selectedVisualItem = visualItems?.find(
        (item) => item.assetName === fileName,
      );

      // If the asset is not in the visuals, add it
      if (!selectedVisualItem) {
        selectedVisualItem = {
          visible: true,
          assetName: selectedAsset?.fileName,
        };

        const filteredItems = visualItems?.filter(
          (item) => item.assetName !== fileName,
        );

        if (filteredItems) {
          const updatedVisualsItems = [
            ...filteredItems,
            ...[selectedVisualItem],
          ];

          updateVisualItems(updatedVisualsItems);
        } else {
          updateVisualItems([selectedVisualItem]);
        }
      }
    };

    const renderVisualItem = (
      item: OptionalVisualItemsInterface,
      index: number,
    ) => (
      <GridContainer container spacing={2} key={item?.assetName! + index}>
        <GridVisualPath xs={12} item>
          <Grid xs={10} style={{ wordBreak: 'break-all' }}>
            {item.assetName}
          </Grid>

          {userRole !== AuthClassification.OWNER_OPERATOR && (
            <Grid xs={2}>
              <Button
                variant="text"
                color="primary"
                onClick={() => deleteVisualItem(item.assetName!)}>
                <DeleteOutlinedIcon color="error" />
              </Button>
            </Grid>
          )}
        </GridVisualPath>
        <Grid item xs={6}>
          <NumberInputWidget
            value={item?.style?.width || ''}
            label={lang.width}
            options={{ suffix: 'px' }}
            onChange={(value) => {
              onChangedField(item.assetName!, 'width', value as string);
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <NumberInputWidget
            value={item?.style?.height || ''}
            label={lang.height}
            options={{ suffix: 'px' }}
            onChange={(value) => {
              onChangedField(item.assetName!, 'height', value as string);
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <NumberInputWidget
            label={lang.xAxis}
            options={{ suffix: 'px' }}
            value={item?.style?.left || ''}
            onChange={(value) => {
              onChangedField(item.assetName!, 'left', value as string);
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <NumberInputWidget
            label={lang.yAxis}
            options={{ suffix: 'px' }}
            value={item?.style?.top || ''}
            onChange={(value) => {
              onChangedField(item.assetName!, 'top', value as string);
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <NumberInputWidget
            label={lang.zIndex}
            value={item?.style?.zIndex || ''}
            onChange={(value) => {
              onChangedField(item.assetName!, 'zIndex', value as string);
            }}
          />
        </Grid>
      </GridContainer>
    );

    return (
      <Box ref={ref}>
        {visualItems?.map((visual, index) => renderVisualItem(visual, index))}
        {userRole !== AuthClassification.OWNER_OPERATOR && (
          <div>
            <Button
              variant="text"
              color="primary"
              onClick={() => setShowResourceSelector(true)}>
              + Add images
            </Button>
          </div>
        )}

        {showResourceSelector && (
          <FormControlLabel
            control={
              <ResourceSelectorSidebar
                label="Select Image"
                type={ResourceType.PRODUCT}
                openResourceSelectorDrawer={showResourceSelector}
                onToggleResourceSelectorDrawer={() =>
                  setShowResourceSelector(!showResourceSelector)
                }
                onChange={(value) => {
                  onAddAsset(value);
                }}
              />
            }
            label="Select Image"
          />
        )}
      </Box>
    );
  },
);
